/**
 * Bridge between the Keycloak `AuthProvider` and the axios response
 * interceptor. The provider registers its refresh handler here; the
 * interceptor calls `refreshAccessToken` when a request fails with 401 so a
 * stale token is silently renewed and the request retried once.
 */

export interface IAuthTokenRefresher {
  /** Renews the access token. Returns true when a fresh token is available. */
  refresh: () => Promise<boolean>;
}

let refresher: IAuthTokenRefresher | null = null;

export const setAuthTokenRefresher = (next: IAuthTokenRefresher | null) => {
  refresher = next;
};

// Single-flight guard so concurrent 401s share one refresh instead of racing
// Keycloak `updateToken` N times.
let refreshInFlight: Promise<boolean> | null = null;

export const refreshAccessToken = async (): Promise<boolean> => {
  if (!refresher) return false;

  if (!refreshInFlight) {
    refreshInFlight = refresher
      .refresh()
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};
